import React from 'react'
import styled from '@emotion/styled'
import {
  useFloating,
  autoUpdate,
  offset,
  type Placement,
  shift,
  autoPlacement,
} from '@floating-ui/react-dom'
import { colors, font, layering, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'

export const ContextMenu: React.FC<{
  isOpen: boolean
  /** Popover will be placed relative to reference element */
  referenceElRef: React.RefObject<HTMLElement>
  /** Relative to reference, where should popover be placed */
  placement?: Placement
  /** Offset in pixels from reference on main axis */
  offsetYPx?: number
  /** Offset in pixels from reference on cross axis */
  offsetXPx?: number
  /** Allow auto placement with most space available */
  isAutoPlacement?: boolean
  children?: React.ReactNode
}> = ({
  isOpen,
  referenceElRef,
  placement = 'bottom',
  offsetYPx = 0,
  offsetXPx = 0,
  isAutoPlacement = false,
  children,
}) => {
  if (!isOpen) return null

  return (
    <ContextMenuComponent
      referenceElRef={referenceElRef}
      placement={placement}
      offsetYPx={offsetYPx}
      offsetXPx={offsetXPx}
      isAutoPlacement={isAutoPlacement}
    >
      {children}
    </ContextMenuComponent>
  )
}

// ==========================
// Local Component w/ Content
// ==========================

const ContextMenuComponent: React.FC<Omit<React.ComponentProps<typeof ContextMenu>, 'isOpen'>> = ({
  referenceElRef,
  placement,
  offsetYPx,
  offsetXPx,
  isAutoPlacement,
  children,
}) => {
  const middleware = React.useMemo(() => {
    const extras = [
      offset({
        mainAxis: offsetYPx ?? 0,
        crossAxis: offsetXPx ?? 0,
      }),
      shift(),
    ]
    if (isAutoPlacement) extras.push(autoPlacement())
    return extras
  }, [isAutoPlacement, offsetYPx, offsetXPx])

  const { floatingStyles, refs } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
    elements: { reference: referenceElRef.current },
  })

  return (
    <ContextMenuStyled
      ref={refs.setFloating}
      data-testid='Overlay.ContextMenu'
      style={floatingStyles}
    >
      {children}
    </ContextMenuStyled>
  )
}

// ======
// Styles
// ======

const ContextMenuStyled = styled.div({
  zIndex: layering.pagePopoverElements,
  display: 'flex',
  flexDirection: 'column',
  padding: spacing.smx1,
  background: colors.white,
  alignItems: 'center',
  gap: spacing.smx1,
  border: `0.1rem solid ${colors.neutral[200]}`,
  borderRadius: radius.lgx2,
  boxShadow: shadow.lg,
  boxSizing: 'border-box',

  '> h4': {
    padding: `0 ${spacing.smx2}`,
    margin: 0,
    marginBottom: spacing.smx2,
    color: colors.black,
    fontFamily: font.family.primary,
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    fontWeight: font.weight.semibold,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },

  '> button': {
    display: 'flex',
    alignSelf: 'stretch',
    justifyContent: 'center',

    '> div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'stretch',
      width: '100%',
      gap: spacing.smx1,
    },
  },

  '@keyframes animateIn': {
    ['0%']: { transform: 'scale(0.92)', opacity: 0 },
    ['70%']: { opacity: 1 },
    ['100%']: { transform: 'translateY(0)' },
  },
})
